import { useEffect } from 'react';

import { STORAGE_KEYS, STORAGE_MESSAGE_TYPES } from '@/utils/constants';

type SearchStartMessageCallbacks = {
  onSearchQuery: (payload: string) => void;
  onStoredQueries: (queries: string[]) => void;
};

/**
 * Subscribes to window/document message events for SearchStart:
 * - SEARCH_QUERY: updates search input via onSearchQuery(payload)
 * - GET_RESPONSE_STORAGE (MULTI_SEARCH_QUERIES): syncs stored multi-search queries via onStoredQueries(queries)
 */
export function useSearchStartMessages({
  onSearchQuery,
  onStoredQueries,
}: SearchStartMessageCallbacks): void {
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.data?.type === 'SEARCH_QUERY') {
        onSearchQuery(event.data.payload);
      }

      try {
        const parsedData =
          typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        if (
          parsedData?.type === STORAGE_MESSAGE_TYPES.GET_RESPONSE_STORAGE &&
          parsedData?.data?.key === STORAGE_KEYS.MULTI_SEARCH_QUERIES
        ) {
          const queriesVal = parsedData.data?.value;
          if (
            queriesVal &&
            typeof queriesVal === 'string' &&
            queriesVal.length > 0
          ) {
            onStoredQueries(queriesVal.split(','));
          } else {
            onStoredQueries([]);
          }
        }
      } catch {
        // Ignore parsed JSON errors
      }
    };

    window.addEventListener('message', handler);
    document.addEventListener('message', handler as EventListener);
    return () => {
      window.removeEventListener('message', handler);
      document.removeEventListener('message', handler as EventListener);
    };
  }, [onSearchQuery, onStoredQueries]);
}
